import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction pour obtenir la date/heure actuelle au format datetime-local
const getCurrentDateTime = () => {
  const now = new Date();
  // Format: YYYY-MM-DDTHH:MM
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const LivraisonModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  // Delivery info - Pré-rempli avec la date/heure actuelle
  const [deliveryTime, setDeliveryTime] = useState(getCurrentDateTime());
  const [recipientName, setRecipientName] = useState('');
  const [recipientFunction, setRecipientFunction] = useState('');
  const [hasReservation, setHasReservation] = useState(false);
  const [reservationDetails, setReservationDetails] = useState('');
  
  // Documents
  const [bonLivraison, setBonLivraison] = useState(null);
  const [photosLivraison, setPhotosLivraison] = useState([]);

  // Réinitialiser la date/heure à l'ouverture du modal
  useEffect(() => {
    if (open) {
      setDeliveryTime(getCurrentDateTime());
    }
  }, [open]);

  const handlePhotosChange = (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    
    const oversized = fileArray.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      toast({
        title: "Fichiers trop volumineux",
        description: "La taille maximale par photo est de 10MB",
        variant: "destructive"
      });
      return;
    }
    
    setPhotosLivraison(fileArray);
  };

  const validateForm = () => {
    if (!deliveryTime) {
      toast({
        title: "Champ manquant",
        description: "L'heure de livraison est obligatoire",
        variant: "destructive"
      });
      return false;
    }
    
    // Nom du destinataire - OPTIONNEL
    
    if (hasReservation && !reservationDetails.trim()) {
      toast({
        title: "Champ manquant",
        description: "Veuillez préciser les détails des réserves",
        variant: "destructive"
      });
      return false;
    }
    
    // Bon de livraison - OPTIONNEL
    // Photos de déchargement - OPTIONNEL
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Step 1: Create logistics info
      const logisticsData = {
        workflowStep: 'livree',
        deliveryTime: new Date(deliveryTime).toISOString(),
        recipientName: recipientName,
        recipientFunction: recipientFunction || null,
        hasReservation: hasReservation,
        reservationDetails: hasReservation ? reservationDetails : null
      };
      
      await axios.post(
        `${API}/operations/${operation.id}/logistics`,
        logisticsData
      );
      
      // Step 2: Upload bon de livraison (si fourni)
      if (bonLivraison) {
        const bonFormData = new FormData();
        bonFormData.append('file', bonLivraison);
        bonFormData.append('documentType', 'bon_livraison');
        
        await axios.post(
          `${API}/operations/${operation.id}/documents`,
          bonFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      // Step 3: Upload photos
      const photoPromises = photosLivraison.map(photo => {
        const formData = new FormData();
        formData.append('file', photo);
        formData.append('documentType', 'photos_livraison');
        
        return axios.post(
          `${API}/operations/${operation.id}/documents`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      });
      
      await Promise.all(photoPromises);
      
      // Step 4: Change status using new endpoint
      const { changeOperationStatus } = await import('../utils/statusChangeHelper');
      await changeOperationStatus(operation.id, 'livree', {
        delivery_time: deliveryTime,
        recipient_name: recipientName,
        recipient_function: recipientFunction,
        has_reservation: hasReservation,
        reservation_details: reservationDetails
      });
      
      toast({
        title: "✅ Livraison confirmée",
        description: "L'opération a été marquée comme livrée"
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error submitting livraison:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirmation de Livraison</DialogTitle>
          <DialogDescription>
            Enregistrer les informations de livraison
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-[#1db584]" />
            <h3 className="text-lg font-semibold">Informations de Livraison</h3>
          </div>
          
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Tous les champs marqués * sont obligatoires
            </AlertDescription>
          </Alert>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="deliveryTime">Heure de livraison *</Label>
              <Input
                id="deliveryTime"
                type="datetime-local"
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="recipientName">Nom du destinataire (optionnel)</Label>
              <Input
                id="recipientName"
                placeholder="Nom de la personne qui réceptionne"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="recipientFunction">Fonction / Service (optionnel)</Label>
              <Input
                id="recipientFunction"
                placeholder="ex: Responsable logistique"
                value={recipientFunction}
                onChange={(e) => setRecipientFunction(e.target.value)}
              />
            </div>
            
            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded">
              <Checkbox
                id="hasReservation"
                checked={hasReservation}
                onCheckedChange={setHasReservation}
              />
              <Label htmlFor="hasReservation" className="cursor-pointer">
                Présence de réserves
              </Label>
            </div>
            
            {hasReservation && (
              <div>
                <Label htmlFor="reservationDetails">Détail des réserves *</Label>
                <Textarea
                  id="reservationDetails"
                  placeholder="Décrire les réserves émises lors de la livraison..."
                  value={reservationDetails}
                  onChange={(e) => setReservationDetails(e.target.value)}
                  rows={3}
                  required
                />
              </div>
            )}
          </div>
          
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Upload className="w-5 h-5 text-[#1db584]" />
              <h3 className="text-lg font-semibold">Documents</h3>
            </div>
            
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="bonLivraison" className="flex items-center gap-2">
                  📄 Bon de déchargement (optionnel)
                </Label>
                {bonLivraison && <CheckCircle2 className="w-5 h-5 text-green-600" />}
              </div>
              
              <Input
                id="bonLivraison"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setBonLivraison(e.target.files[0])}
                className="cursor-pointer"
              />
              
              {bonLivraison && (
                <p className="text-sm text-gray-600 mt-1">
                  {bonLivraison.name}
                </p>
              )}
            </div>
            
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="photosLivraison" className="flex items-center gap-2">
                  📸 Photos du déchargement (optionnel)
                </Label>
                {photosLivraison.length > 0 && (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                )}
              </div>
              
              <Input
                id="photosLivraison"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotosChange(e.target.files)}
                className="cursor-pointer"
              />
              
              {photosLivraison.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {photosLivraison.length} photo(s) sélectionnée(s)
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Traitement...' : 'Confirmer la livraison'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LivraisonModal;